import {
  Avatar,
  Box,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { orange } from '@mui/material/colors';
import Container from '@mui/material/Container';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getInitials } from '../../components/Header/Header';
import { Heading } from '../../components/Heading';
import { Spinner } from '../../components/Spinner';
import { useAuth } from '../../contexts/authContext';
import { useTheme } from '../../contexts/themeContext';
import {
  getRankedMatches,
  getUsers,
  LeaderboardUser,
  MatchResponse,
  User,
} from '../../firebase/api';
import capsatIcon from '../../icons/capsat-icon-monochrome.png';
import DinoCapsMedalIcon from '../../icons/DinoCapsMedalIcon';
import MedalIcon from '../../icons/MedalIcon';
import { getRatingType } from '../ProfilePage/ProfilePage';

function isSearchMatch(user: User, searchQuery: string): boolean {
  return (
    user.nameLabel?.toLowerCase().includes(searchQuery.toLowerCase()) ?? false
  );
}

const styles = {
  tabs: {
    width: '100%',

    '& > div': {
      borderBottom: 1,
      borderColor: 'divider',
    },

    '& .MuiTab-root': {
      minWidth: 0,
      flexGrow: 1,
      flexBasis: 0,
      maxWidth: 'none',
    },
  },
  tableContainer: {
    maxHeight: 'calc(100vh - 350px)',
  },
  tableRow: {
    '&:last-child td': {
      borderBottom: 0,
    },
  },
  selfTableRow: {
    position: 'sticky',
    bottom: 0,
    top: 31,
    backgroundColor: 'background.paper',
    zIndex: 1,
    borderRadius: 1,
    boxShadow:
      '0px 3px 1px -2px rgba(0, 0, 0, 0.2), 0px 2px 2px 0px rgba(0, 0, 0, 0.14), 0px 1px 5px 0px rgba(0, 0, 0, 0.12)',

    '& td': {
      borderBottom: 0,
    },
  },
  hideBorderBelow: {
    '& td': {
      borderBottom: 0,
    },
  },
  borderLeftRadius: {
    borderTopLeftRadius: 4,
    borderBottomLeftRadius: 4,
  },
  borderRightRadius: {
    borderTopRightRadius: 4,
    borderBottomRightRadius: 4,
  },
  top3: {
    borderRadius: '50%',
    paddingRight: 0,
    paddingY: 0.5,
    fontSize: 20,
    color: 'rgba(0, 0, 0, 0.8)',
  },
  gold: {
    backgroundColor: '#F7C631',
    border: '2px solid #E3AA24',
  },
  silver: {
    backgroundColor: '#D1CCC5',
    border: '2px solid #B3ACA8',
  },
  bronze: {
    backgroundColor: '#CE834A',
    border: '2px solid #B37314',
  },
  twoLineTypography: {
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    lineHeight: 1.4,
  },
};

export const LeaderboardPage = () => {
  const { user: self } = useAuth();
  const navigate = useNavigate();
  const { darkModeEnabled } = useTheme();
  const [users, setUsers] = useState<readonly User[] | undefined>(undefined);
  const [matches, setMatches] = useState<readonly MatchResponse[] | undefined>(
    undefined,
  );
  const [currentTab, setCurrentTab] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');

  const ratingType = getRatingType(currentTab);
  const ratingField = ('rating' + (ratingType ?? '')) as
    | 'rating'
    | 'rating7'
    | 'rating9'
    | 'rating11'
    | 'rating13';

  const leaderboardWinnersAndLosers = useMemo(() => {
    if (matches === undefined) return undefined;

    return new Set(
      matches
        .filter(
          match =>
            ratingType === undefined || ratingType === match.numberOfGlasses,
        )
        .flatMap(match => [match.winnerId, match.loserId]),
    );
  }, [matches, ratingType]);

  const usersWithMatches = useMemo(() => {
    if (users === undefined || leaderboardWinnersAndLosers === undefined)
      return undefined;

    return users
      .filter(user => leaderboardWinnersAndLosers.has(user.id))
      .toSorted((a, b) => b[ratingField] - a[ratingField]);
  }, [leaderboardWinnersAndLosers, ratingField, users]);

  const leaderboardUsers = useMemo(() => {
    if (usersWithMatches === undefined) return undefined;

    let prevRating = -1;
    let prevRanking = -1;
    const filteredUsers: LeaderboardUser[] = [];
    for (const [index, user] of usersWithMatches.entries()) {
      const rating = user[ratingField];
      const ranking = rating !== prevRating ? index + 1 : prevRanking;

      if (searchQuery === '' || isSearchMatch(user, searchQuery)) {
        const isTop3 = ranking <= 3;
        const isSelf = user.id === self?.id;

        filteredUsers.push({ ...user, ranking, isTop3, isSelf });
      }

      prevRating = rating;
      prevRanking = ranking;
    }

    return filteredUsers;
  }, [usersWithMatches, ratingField, searchQuery, self?.id]);

  const loading = leaderboardUsers !== undefined;

  useEffect(() => {
    const fetchUsers = async () => {
      const fetchedUsers = await getUsers();
      setUsers(fetchedUsers);
    };

    const fetchMatches = async () => {
      const fetchedMatches = await getRankedMatches();
      setMatches(fetchedMatches);
    };

    fetchUsers();
    fetchMatches();
  }, []);

  return (
    <Container component="main" maxWidth="sm">
      <Stack gap={4} py={2}>
        <Heading>Topplista</Heading>

        <Stack gap={2}>
          <Tabs
            value={currentTab}
            onChange={(_, newValue) => setCurrentTab(newValue)}
            aria-label="tabs"
            sx={styles.tabs}
          >
            <Tab
              value={0}
              icon={<img src={capsatIcon} alt="Capsat icon" width="26px" />}
              aria-label="main rating"
            />
            <Tab value={1} label="7" />
            <Tab value={2} label="9" />
            <Tab value={3} label="11" />
            <Tab value={4} label="13" />
          </Tabs>

          <Stack gap={2} mx={1}>
            <TextField
              label="Sök"
              type="search"
              value={searchQuery}
              onChange={event => setSearchQuery(event.target.value)}
              size="small"
            />

            {loading ? (
              <TableContainer sx={styles.tableContainer}>
                <Table size="small" aria-label="leaderboard" stickyHeader>
                  <TableHead>
                    <TableRow
                      sx={{ '& > th': { bgcolor: 'background.default' } }}
                    >
                      <TableCell sx={{ minWidth: 57.6 }} />
                      <TableCell align="left" sx={{ px: 0, pt: 0 }}>
                        Capsare
                      </TableCell>
                      <TableCell sx={{ width: '100%' }} />
                      <TableCell sx={{ pt: 0, pl: 0, pr: 1.6 }}>
                        Rating
                      </TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {leaderboardUsers.map((user, index) => {
                      const userAbove = leaderboardUsers[index - 1];
                      const userBelow = leaderboardUsers[index + 1];
                      const rating = user[ratingField];

                      const isRatingSameAsAbove =
                        userAbove?.[ratingField] === rating;
                      const isRatingSameAsBelow =
                        userBelow?.[ratingField] === rating;
                      const isBelowSelf = userAbove?.id === self?.id;
                      const hideFields = isRatingSameAsAbove && !user.isSelf;

                      return (
                        <TableRow
                          key={user.nameLabel}
                          onClick={() =>
                            navigate(
                              user.isSelf ? '/profile' : `/user/${user.id}`,
                              { state: { prevPathname: '/leaderboard' } },
                            )
                          }
                          sx={[
                            styles.tableRow,
                            user.isSelf && styles.selfTableRow,
                            isRatingSameAsBelow && styles.hideBorderBelow,
                            isBelowSelf &&
                              !isRatingSameAsAbove && {
                                '& td': {
                                  borderTop: `1px solid ${
                                    darkModeEnabled
                                      ? 'rgba(81, 81, 81, 1)'
                                      : 'rgba(224, 224, 224, 1)'
                                  }`,
                                },
                              },
                          ]}
                        >
                          <TableCell
                            align="center"
                            sx={[
                              { pl: 0.6, pr: 1.6 },
                              user.isSelf && styles.borderLeftRadius,
                            ]}
                          >
                            {!hideFields && (
                              <Box
                                sx={[
                                  { width: 40 },
                                  user.isTop3 && styles.top3,
                                  user.ranking === 1 && styles.gold,
                                  user.ranking === 2 && styles.silver,
                                  user.ranking === 3 && styles.bronze,
                                ]}
                              >
                                {!user.isTop3 && '#'}
                                {user.ranking}
                              </Box>
                            )}
                          </TableCell>
                          <TableCell sx={{ pl: 0 }}>
                            {user.profilePicture !== undefined ? (
                              <Avatar
                                src={user.profilePicture}
                                variant="rounded"
                              />
                            ) : (
                              <Avatar
                                variant="rounded"
                                sx={{ bgcolor: orange[700] }}
                              >
                                {getInitials(user)}
                              </Avatar>
                            )}
                          </TableCell>
                          <TableCell sx={{ pl: 0 }}>
                            <Box display="flex" alignItems="center">
                              {user.numberOfGlassesMedal !== undefined && (
                                <MedalIcon>
                                  {user.numberOfGlassesMedal}
                                </MedalIcon>
                              )}
                              {user?.dinoCapsMedal === true && (
                                <DinoCapsMedalIcon />
                              )}

                              <Box
                                ml={
                                  user.numberOfGlassesMedal !== undefined ||
                                  user?.dinoCapsMedal === true
                                    ? 0.5
                                    : 0
                                }
                              >
                                <Typography variant="body2">
                                  {user.nameLabel}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={styles.twoLineTypography}
                                >
                                  {user.associationLabel}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>

                          <TableCell
                            align="right"
                            sx={[
                              { pl: 0, pr: 1.6 },
                              user.isSelf && styles.borderRightRadius,
                            ]}
                          >
                            {!hideFields && (
                              <Typography variant="body2">{rating}</Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Spinner />
            )}
          </Stack>
        </Stack>
      </Stack>
    </Container>
  );
};
